// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { z } from 'zod';
import { SemanticJsonNumberTokenPattern } from './lossless-json-codec';

export type JsonNumberToken = string;

/**
 * Reader-only structural view of a lossless numeric value materialized from
 * stored JSON. Current writer and request contracts must not use this type.
 */
export interface StoredLosslessNumber {
    readonly isLosslessNumber: true;
    readonly value: JsonNumberToken;
}

export type StoredJsonNumber = number | StoredLosslessNumber;

export type StoredJsonNumberConversionContract =
    | {
          readonly domain: string;
          readonly numericKind: 'safe-integer';
          readonly minimum: number;
          readonly maximum: number;
      }
    | {
          readonly domain: string;
          /** Caller review must document why binary64 precision is adequate. */
          readonly numericKind: 'bounded-binary64';
          readonly minimum: number;
          readonly maximum: number;
      };

export type StoredJsonNumberConversionFailureReason =
    | 'invalid-stored-number'
    | 'invalid-conversion-contract'
    | 'overflow'
    | 'underflow'
    | 'non-integer'
    | 'unsafe-integer'
    | 'out-of-range';

export type StoredJsonNumberConversionResult =
    | {
          readonly success: true;
          readonly value: number;
          readonly token: JsonNumberToken;
      }
    | {
          readonly success: false;
          readonly reason: StoredJsonNumberConversionFailureReason;
          readonly domain: string;
          readonly token?: JsonNumberToken;
      };

export const JsonNumberTokenPattern: RegExp = SemanticJsonNumberTokenPattern;

export const JsonNumberTokenSchema: z.ZodType<JsonNumberToken> = z
    .string()
    .regex(JsonNumberTokenPattern, 'Invalid JSON number token');

export const StoredLosslessNumberMarkerSchema: z.ZodType<true> = z.literal(true);

export const StoredLosslessNumberShapeSchema: z.ZodType<StoredLosslessNumber> = z.looseObject({
    isLosslessNumber: StoredLosslessNumberMarkerSchema,
    value: JsonNumberTokenSchema,
});

function isStoredLosslessNumber(value: unknown): value is StoredLosslessNumber {
    return StoredLosslessNumberShapeSchema.safeParse(value).success;
}

export const StoredLosslessNumberSchema: z.ZodType<StoredLosslessNumber> =
    z.custom<StoredLosslessNumber>(isStoredLosslessNumber, {
        message: 'Invalid stored lossless number',
    });

export const StoredNativeNumberSchema: z.ZodType<number> = z.number().finite();

export const StoredJsonNumberSchema: z.ZodType<StoredJsonNumber> = z.union([
    StoredNativeNumberSchema,
    StoredLosslessNumberSchema,
]);

export const StoredJsonNumberRecordSchema: z.ZodType<Record<string, unknown>> =
    z.record(z.string(), z.unknown());

export interface StoredJsonSafeIntegerFieldContract {
    readonly domain: string;
    readonly fields: readonly string[];
    readonly minimum: number;
    readonly maximum: number;
}

export interface StoredJsonSafeIntegerPathContract
    extends StoredJsonSafeIntegerFieldContract {
    readonly path: readonly string[];
}

export type StoredJsonNumberFieldConversionContract =
    StoredJsonNumberConversionContract & {
        readonly fields: readonly string[];
    };

export type StoredJsonNumberPathConversionContract =
    StoredJsonNumberFieldConversionContract & {
        readonly path: readonly string[];
    };

export function extractStoredJsonNumberToken(value: StoredJsonNumber): JsonNumberToken {
    const losslessNumberResult = StoredLosslessNumberSchema.safeParse(value);
    return losslessNumberResult.success ? losslessNumberResult.data.value : String(value);
}

function isZeroJsonNumberToken(token: JsonNumberToken): boolean {
    const coefficient = token.replace(/^[+-]/u, '').split(/[eE]/u, 1)[0]?.replace('.', '');
    return coefficient !== undefined && /^0+$/u.test(coefficient);
}

function isExactIntegerJsonNumberToken(token: JsonNumberToken): boolean {
    const match =
        /^(?:-)?(?<integer>0|[1-9]\d*)(?:\.(?<fraction>\d+))?(?:[eE](?<exponent>[+-]?\d+))?$/u.exec(
            token
        );
    if (match?.groups === undefined) {
        return false;
    }

    const fraction = match.groups.fraction ?? '';
    const exponent = Number.parseInt(match.groups.exponent ?? '0', 10);
    const decimalPlacesAfterExponent = fraction.length - exponent;
    if (decimalPlacesAfterExponent <= 0) {
        return true;
    }

    const digits = `${match.groups.integer ?? ''}${fraction}`;
    if (decimalPlacesAfterExponent >= digits.length) {
        return /^0+$/u.test(digits);
    }
    return /^0+$/u.test(digits.slice(digits.length - decimalPlacesAfterExponent));
}

function isValidConversionContract(contract: StoredJsonNumberConversionContract): boolean {
    if (
        contract.domain.trim().length === 0 ||
        !Number.isFinite(contract.minimum) ||
        !Number.isFinite(contract.maximum) ||
        contract.minimum > contract.maximum
    ) {
        return false;
    }
    if (contract.numericKind === 'safe-integer') {
        return Number.isSafeInteger(contract.minimum) && Number.isSafeInteger(contract.maximum);
    }
    return true;
}

function conversionFailure(
    contract: StoredJsonNumberConversionContract,
    reason: StoredJsonNumberConversionFailureReason,
    token?: JsonNumberToken
): StoredJsonNumberConversionResult {
    return {
        success: false,
        reason,
        domain: contract.domain,
        ...(token === undefined ? {} : { token }),
    };
}

export function convertStoredJsonNumberToBoundedNumber(
    value: unknown,
    contract: StoredJsonNumberConversionContract
): StoredJsonNumberConversionResult {
    if (!isValidConversionContract(contract)) {
        return conversionFailure(contract, 'invalid-conversion-contract');
    }

    const storedNumberResult = StoredJsonNumberSchema.safeParse(value);
    if (!storedNumberResult.success) {
        return conversionFailure(contract, 'invalid-stored-number');
    }

    const token = extractStoredJsonNumberToken(storedNumberResult.data);
    const convertedValue = Number(token);
    if (!Number.isFinite(convertedValue)) {
        return conversionFailure(contract, 'overflow', token);
    }
    if (convertedValue === 0 && !isZeroJsonNumberToken(token)) {
        return conversionFailure(contract, 'underflow', token);
    }
    if (contract.numericKind === 'safe-integer') {
        if (!isExactIntegerJsonNumberToken(token)) {
            return conversionFailure(contract, 'non-integer', token);
        }
        if (!Number.isSafeInteger(convertedValue)) {
            return conversionFailure(contract, 'unsafe-integer', token);
        }
    }
    if (convertedValue < contract.minimum || convertedValue > contract.maximum) {
        return conversionFailure(contract, 'out-of-range', token);
    }

    return { success: true, value: convertedValue, token };
}

export function normalizeStoredJsonSafeIntegerObjectFields(
    value: unknown,
    contract: StoredJsonSafeIntegerFieldContract
): unknown {
    return normalizeStoredJsonNumberObjectFields(value, {
        ...contract,
        numericKind: 'safe-integer',
    });
}

export function normalizeStoredJsonNumberObjectFields(
    value: unknown,
    contract: StoredJsonNumberFieldConversionContract
): unknown {
    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!recordResult.success) {
        return value;
    }

    const normalized: Record<string, unknown> = { ...recordResult.data };
    for (const key of Object.keys(recordResult.data)) {
        if (!contract.fields.includes(key)) {
            continue;
        }
        const conversionResult = convertStoredJsonNumberToBoundedNumber(
            recordResult.data[key],
            {
                ...contract,
                domain: `${contract.domain}.${key}`,
            }
        );
        normalized[key] = conversionResult.success
            ? conversionResult.value
            : recordResult.data[key];
    }
    return normalized;
}

export function normalizeStoredJsonSafeIntegerFieldsAtPath(
    value: unknown,
    contract: StoredJsonSafeIntegerPathContract
): unknown {
    return normalizeStoredJsonNumberFieldsAtPath(value, {
        ...contract,
        numericKind: 'safe-integer',
    });
}

export function normalizeStoredJsonNumberFieldsAtPath(
    value: unknown,
    contract: StoredJsonNumberPathConversionContract
): unknown {
    if (contract.path.length === 0) {
        return normalizeStoredJsonNumberObjectFields(value, contract);
    }

    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    const [key, ...remainingPath] = contract.path;
    if (
        !recordResult.success ||
        key === undefined ||
        !Object.hasOwn(recordResult.data, key)
    ) {
        return value;
    }

    return {
        ...recordResult.data,
        [key]: normalizeStoredJsonNumberFieldsAtPath(
            recordResult.data[key],
            {
                ...contract,
                path: remainingPath,
            }
        ),
    };
}
